import { useState, useEffect } from "react";
import { Export, Copy, DownloadSimple, X, FileText, Scroll, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { downloadText, copyText } from "@/lib/io";
import { getRefSig } from "@/lib/attribution";

// iter-40e — Build a seeded share URL so the conversation transcript carries
// the sharer's referral seed everywhere it travels. The Ghost Report and
// affiliate attribution flow read /api/referrals/visit when this URL is
// hit, creating the soft-attribution row that converts to hard-locked
// referral on the visitor's signup.
const buildShareUrl = ({ slug, sig, bookmarkId }) => {
  if (!slug) return "";
  const base = window.location.origin;
  const params = new URLSearchParams();
  // A loaded/saved conversation rides along as ?bookmark=<id> so the recipient
  // lands in the EXACT saved thread (and its free pre-paid audio, if complete).
  if (bookmarkId) params.set("bookmark", bookmarkId);
  // Always carry the sharer's referral seed so attribution survives the share.
  if (sig) { params.set("ref", sig); params.set("from_chat", "1"); }
  const qs = params.toString();
  return qs ? `${base}/s/${slug}?${qs}` : `${base}/s/${slug}`;
};

const buildFooter = (siloName, shareUrl) => {
  const lines = [`\n\n——\nGenerated by My Silo · ${siloName}`];
  if (shareUrl) {
    lines.push(`\nContinue this conversation: ${shareUrl}`);
  }
  return lines.join("");
};

const buildFullTranscript = (messages, siloName, shareUrl) => {
  const lines = messages.map((m) => (m.role === "user" ? `_${m.text}_` : m.text));
  return `${siloName}\nFull Conversation\n\n${lines.join("\n\n")}${buildFooter(siloName, shareUrl)}`;
};

const wrapSummary = (summary, siloName, shareUrl) => `${siloName}\nStrategic Summary\n\n${summary}${buildFooter(siloName, shareUrl)}`;

const handleDownload = async (filename, text) => {
  const ok = await downloadText(filename, text);
  toast[ok ? "success" : "error"](ok ? "Download started." : "Could not start download.");
};

const handleCopy = async (text) => {
  const ok = await copyText(text);
  toast[ok ? "success" : "error"](ok ? "Copied to clipboard." : "Could not copy.");
};

export default function ExportDialog({ open, onClose, messages, siloName = "My Silo", siloSlug = "", summaryEndpoint = "/export/summary", bookmarkId = "" }) {
  const [tab, setTab] = useState("full");
  const [summary, setSummary] = useState("");
  const [loadingSummary, setLoadingSummary] = useState(false);
  // Sprint Feb 8, 2026 — three-tier sharer-seed priority. Logged-in users
  // share with their own referral_code; anonymous viewers carry forward
  // whoever invited them (mysilo_ref_sig cookie); cold visitors share
  // unseeded. Anonymous chain-propagation now actually works.
  const [shareSig, setShareSig] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/me/referral-link");
        if (cancel) return;
        const sig = r.data?.referral_code || r.data?.referrer_sig || "";
        setShareSig(sig);
      } catch {
        // Anonymous — fall back to the cookie-stored inviter seed so the
        // chain doesn't die at this anon visitor.
        if (!cancel) setShareSig(getRefSig() || "");
      }
    })();
    return () => { cancel = true; };
  }, [open]);

  if (!open) return null;

  const shareUrl = buildShareUrl({ slug: siloSlug, sig: shareSig, bookmarkId });
  const fullText = buildFullTranscript(messages, siloName, shareUrl);
  const summaryText = summary ? wrapSummary(summary, siloName, shareUrl) : "";
  const today = new Date().toISOString().slice(0, 10);

  const loadSummary = async () => {
    if (summary || loadingSummary) return;
    setLoadingSummary(true);
    try {
      const r = await api.post(summaryEndpoint, {
        messages: messages.map((m) => ({ role: m.role, text: m.text })),
      });
      setSummary(r.data.summary);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate summary.");
    } finally { setLoadingSummary(false); }
  };

  const switchTab = (t) => { setTab(t); if (t === "summary") loadSummary(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="export-dialog">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="modal-safe relative z-10 w-full max-w-2xl bg-white border border-[#E5E5E0] rounded-xl shadow-2xl overflow-hidden text-[#1C1B1A]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E0]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1C1B1A] text-white flex items-center justify-center">
              <Export size={18} weight="duotone" />
            </div>
            <div>
              <div className="font-serif-display text-lg leading-none">Export & Share</div>
              <div className="text-xs text-[#6B6A68] uppercase tracking-wide mt-1">{siloName}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="export-close" className="text-[#6B6A68] hover:text-[#1C1B1A] p-2 rounded-md hover:bg-[#F0F1EE]"><X size={18} /></button>
        </div>

        <div className="flex border-b border-[#E5E5E0]">
          <button data-testid="export-tab-full" onClick={() => switchTab("full")}
            className={`flex-1 px-5 py-3 text-sm flex items-center justify-center gap-2 border-b-2 -mb-[1px] transition-colors ${tab === "full" ? "border-[#4A5D4E] text-[#1C1B1A]" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
            <Scroll size={16} weight="duotone" /> Full Conversation
          </button>
          <button data-testid="export-tab-summary" onClick={() => switchTab("summary")}
            className={`flex-1 px-5 py-3 text-sm flex items-center justify-center gap-2 border-b-2 -mb-[1px] transition-colors ${tab === "summary" ? "border-[#4A5D4E] text-[#1C1B1A]" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
            <FileText size={16} weight="duotone" /> Strategic Summary
          </button>
        </div>

        <div className="p-6 max-h-[55vh] overflow-y-auto">
          {/* iter-40e — Seeded share link banner. Visible above the tabs so
              the user understands the link they're about to share is
              seeded with their referral code (when logged in). For
              anonymous visitors the seed is missing but the silo-slug
              channel still attributes (auto-follow flow). */}
          {siloSlug && (
            <div className="bg-[#FDFCF8] border border-[#E5E5E0] rounded-md p-3 mb-4 flex items-center gap-3 text-xs" data-testid="export-share-link-banner">
              <LinkIcon size={14} weight="duotone" className="text-[#4A5D4E] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-0.5">
                  Share link · {bookmarkId ? "this saved conversation" : (shareSig ? "seeded with your referral code" : "silo channel only")}
                </div>
                <div className="font-mono text-[#1C1B1A] truncate">{shareUrl || "—"}</div>
              </div>
              <button
                type="button"
                onClick={() => copyText(shareUrl).then((ok) => toast[ok ? "success" : "error"](ok ? "Share link copied." : "Could not copy."))}
                disabled={!shareUrl}
                data-testid="export-share-link-copy"
                className="text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] disabled:opacity-40 flex items-center gap-1 shrink-0"
              >
                <Copy size={11} weight="duotone" /> Copy link
              </button>
            </div>
          )}
          {tab === "full" ? (
            <div data-testid="export-full-preview">
              <p className="text-xs text-[#6B6A68] mb-3">Your questions are italicized; assistant answers appear as standard prose.</p>
              <div className="bg-[#FDFCF8] border border-[#E5E5E0] rounded-md p-5 text-sm leading-relaxed space-y-4">
                {messages.map((m, i) => m.role === "user"
                  ? <p key={i} className="italic text-[#4A5D4E]">{m.text}</p>
                  : <p key={i} className="whitespace-pre-wrap">{m.text}</p>
                )}
              </div>
              <p className="mt-4 text-xs text-[#6B6A68]">Footer included: "Generated by My Silo · {siloName}"</p>
            </div>
          ) : (
            <div data-testid="export-summary-preview">
              {loadingSummary ? (
                <div className="flex items-center gap-2 text-sm text-[#6B6A68] py-10 justify-center">
                  <span className="w-2 h-2 rounded-full bg-[#4A5D4E] animate-bounce" />
                  <span className="w-2 h-2 rounded-full bg-[#4A5D4E] animate-bounce [animation-delay:0.15s]" />
                  <span className="w-2 h-2 rounded-full bg-[#4A5D4E] animate-bounce [animation-delay:0.3s]" />
                  <span className="ml-2">Synthesizing summary…</span>
                </div>
              ) : summary ? (
                <div className="bg-[#FDFCF8] border border-[#E5E5E0] rounded-md p-5 text-sm leading-relaxed whitespace-pre-wrap">{summary}</div>
              ) : (
                <div className="text-sm text-[#6B6A68]">Preparing summary…</div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-[#FDFCF8] border-t border-[#E5E5E0] flex flex-wrap gap-3 justify-end">
          {tab === "full" ? (
            <>
              <button onClick={() => handleCopy(fullText)} data-testid="export-full-copy"
                className="px-5 py-2.5 rounded-md border border-[#E5E5E0] bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] text-sm flex items-center gap-2"><Copy size={16} weight="duotone" /> Copy</button>
              <button onClick={() => handleDownload(`${siloName.toLowerCase().replace(/\s+/g, "-")}-conversation-${today}.txt`, fullText)} data-testid="export-full-download"
                className="px-5 py-2.5 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] text-sm flex items-center gap-2"><DownloadSimple size={16} weight="duotone" /> Download .txt</button>
            </>
          ) : (
            <>
              <button onClick={() => summary && handleCopy(summaryText)} disabled={!summary || loadingSummary} data-testid="export-summary-copy"
                className="px-5 py-2.5 rounded-md border border-[#E5E5E0] bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] text-sm flex items-center gap-2 disabled:opacity-50"><Copy size={16} weight="duotone" /> Copy</button>
              <button onClick={() => summary && handleDownload(`${siloName.toLowerCase().replace(/\s+/g, "-")}-summary-${today}.txt`, summaryText)}
                disabled={!summary || loadingSummary} data-testid="export-summary-download"
                className="px-5 py-2.5 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] text-sm flex items-center gap-2 disabled:opacity-50"><DownloadSimple size={16} weight="duotone" /> Download .txt</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
